/**
 * 게시판 에디터 툴바 선택지.
 * 값(value)은 본문 JSON 에 그대로 저장되므로, 바꾸면 기존 글의 선택 상태 표시만 어긋난다(렌더는 유지).
 */

interface EditorOption {
  value: string;
  label: string;
}

/** 본문 제목 단계 — h1 은 게시글 제목이 쓴다. */
export const BOARD_HEADING_LEVELS = [2, 3, 4] as const;
export type BoardHeadingLevel = (typeof BOARD_HEADING_LEVELS)[number];

/** 문단 스타일 — 본문 h1 은 게시글 제목이 쓰므로 h2~h4 만 연다. */
export const BLOCK_STYLES = [
  { value: 'p', label: '본문' },
  { value: '2', label: '제목 1' },
  { value: '3', label: '제목 2' },
  { value: '4', label: '제목 3' },
] as const satisfies readonly EditorOption[];

export type BlockStyle = (typeof BLOCK_STYLES)[number]['value'];

/** 글꼴 — 웹폰트 없이 OS 글꼴에 기대므로 없는 환경에선 다음 후보로 떨어진다. */
export const FONT_FAMILIES = [
  { value: "'Malgun Gothic', '맑은 고딕', sans-serif", label: '맑은 고딕' },
  { value: "'Nanum Gothic', '나눔고딕', sans-serif", label: '나눔고딕' },
  { value: "Gulim, '굴림', sans-serif", label: '굴림' },
  { value: "Dotum, '돋움', sans-serif", label: '돋움' },
  { value: "Batang, '바탕', serif", label: '바탕' },
  { value: "Gungsuh, '궁서', serif", label: '궁서' },
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: "'Times New Roman', Times, serif", label: 'Times New Roman' },
  { value: "'Courier New', Courier, monospace", label: 'Courier New' },
] as const satisfies readonly EditorOption[];

/** 글자 크기(pt) — Outlook 과 같은 단계 */
export const FONT_SIZES = [
  '8', '9', '10', '10.5', '11', '12', '14', '16', '18', '20', '24', '28', '36', '48', '72',
].map((pt) => ({ value: `${pt}pt`, label: pt })) satisfies EditorOption[];

export const LINE_HEIGHTS = [
  { value: '1', label: '1.0' },
  { value: '1.15', label: '1.15' },
  { value: '1.5', label: '1.5' },
  { value: '2', label: '2.0' },
  { value: '2.5', label: '2.5' },
  { value: '3', label: '3.0' },
] as const satisfies readonly EditorOption[];

/** 글자색 — 회색조 / 연한 색 / 표준 색(Outlook 표준) / 진한 색 */
export const TEXT_COLOR_ROWS: readonly (readonly string[])[] = [
  ['#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff'],
  ['#f2dcdb', '#ffcccc', '#fff2cc', '#ffffcc', '#e2efd9', '#d5f5e3', '#ddebf7', '#dbe5f1', '#d9d9f3', '#e4dfec'],
  ['#c00000', '#ff0000', '#ffc000', '#ffff00', '#92d050', '#00b050', '#00b0f0', '#0070c0', '#002060', '#7030a0'],
  ['#7f0000', '#990000', '#bf8f00', '#999900', '#4f6228', '#00703c', '#007aa6', '#1f4e79', '#001233', '#3f1f5c'],
];

/** 형광펜 — 다크 모드에선 형광 위 글자를 어둡게 고정하므로(index.css) 연한 색만 둔다. */
export const HIGHLIGHT_COLOR_ROWS: readonly (readonly string[])[] = [
  ['#fff59d', '#c5e1a5', '#b2ebf2', '#bbdefb', '#d1c4e9', '#f8bbd0', '#ffccbc', '#e0e0e0'],
];

/** 특수 문자 — 공지·자료실에서 자주 쓰는 기호와 건설 단위 */
export const SPECIAL_CHARS: readonly string[] = [
  '※', '★', '☆', '●', '○', '◎', '◆', '◇', '■', '□', '▲', '△', '▼', '▽', '▶', '▷',
  '◀', '◁', '→', '←', '↑', '↓', '↔', '⇒', '⇔', '✓', '✔', '✕', '☎', '☞', '♥', '♣',
  '·', '…', '‘', '’', '“', '”', '「', '」', '『', '』', '【', '】', '〈', '〉', '《', '》',
  '①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩', '㉠', '㉡', '㉢', '㉣', '㉤', '㉥',
  '㎜', '㎝', '㎞', '㎡', '㎥', '㎏', '㎾', '℃', '°', '±', '×', '÷', '≠', '≤', '≥', '∞',
  '₩', '$', '€', '¥', '£', '%', '‰', '©', '®', '™', '§', '¶', '†', '‡', '½', '¼',
];

/** 표 삽입 격자 최대 크기 */
export const TABLE_GRID_MAX = 8;
